<script>
// utils and composables
import { login } from '../../api/auth';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';
import AnalyticsHelper from 'dashboard/helper/AnalyticsHelper';
import { SESSION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

// components
import SimpleDivider from '../../components/Divider/SimpleDivider.vue';
import FormInput from '../../components/Form/Input.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';
import SessionLimitOverlay from 'dashboard/components/auth/SessionLimitOverlay.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
  'saml-authentication-failed': 'LOGIN.SAML.API.ERROR_MESSAGE',
  'saml-not-enabled': 'LOGIN.SAML.API.ERROR_MESSAGE',
};

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';
const USER_NOT_CONFIRMED_ERROR_CODE = 'user_not_confirmed';

export default {
  components: {
    FormInput,
    GoogleOAuthButton,
    Spinner,
    NextButton,
    SimpleDivider,
    MfaVerification,
    SessionLimitOverlay,
    Icon,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isPasswordVisible: false,
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      mfaRequired: false,
      mfaToken: null,
      sessionsLimitReached: false,
      limitedSessions: [],
    };
  },
  validations() {
    return {
      credentials: {
        password: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    allowedLoginMethods() {
      return window.osdeskConfig.allowedLoginMethods || ['email'];
    },
    showGoogleOAuth() {
      return (
        this.allowedLoginMethods.includes('google_oauth') &&
        Boolean(window.osdeskConfig.googleOAuthClientId)
      );
    },
    showSignupLink() {
      return window.osdeskConfig.signupEnabled === 'true';
    },
    showSamlLogin() {
      return this.allowedLoginMethods.includes('saml');
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const messageKey = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      const translatedMessage = this.getTranslatedMessage(messageKey);
      useAlert(translatedMessage);
      this.requestIdleCallbackPolyfill(() => {
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  methods: {
    getTranslatedMessage(key) {
      switch (key) {
        case 'LOGIN.OAUTH.NO_ACCOUNT_FOUND':
          return this.$t('LOGIN.OAUTH.NO_ACCOUNT_FOUND');
        case 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY':
          return this.$t('LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY');
        case 'LOGIN.API.UNAUTH':
        default:
          return this.$t('LOGIN.API.UNAUTH');
      }
    },
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },
    handleImpersonation() {
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONATION_URL_SEARCH_KEY);
      if (impersonation) {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },
    submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      login(credentials)
        .then(result => {
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            return;
          }

          if (result?.sessionsLimitReached) {
            this.loginApi.showLoading = false;
            this.sessionsLimitReached = true;
            this.limitedSessions = result.sessions;
            AnalyticsHelper.track(SESSION_EVENTS.LIMIT_HIT);
            return;
          }

          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          if (response?.errorCode === USER_NOT_CONFIRMED_ERROR_CODE) {
            this.loginApi.showLoading = false;
            this.$router.push({
              name: 'auth_verify_email',
              state: { email: credentials.email },
            });
            return;
          }

          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }

      this.submitLogin();
    },
    handleMfaVerified() {
      this.handleImpersonation();
      window.location = '/app';
    },
    handleMfaCancel() {
      this.mfaRequired = false;
      this.mfaToken = null;
      this.credentials.password = '';
    },
    retryLoginWithParams(extraParams) {
      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
        ...extraParams,
      };

      this.sessionsLimitReached = false;
      this.limitedSessions = [];
      this.loginApi.showLoading = true;
      login(credentials)
        .then(result => {
          if (result?.sessionsLimitReached) {
            this.loginApi.showLoading = false;
            this.sessionsLimitReached = true;
            this.limitedSessions = result.sessions;
            AnalyticsHelper.track(SESSION_EVENTS.LIMIT_HIT);
            return;
          }
          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    handleSessionRevoke(sessionId) {
      this.retryLoginWithParams({ revoke_session_id: sessionId });
    },
    handleSessionRevokeAll() {
      this.retryLoginWithParams({ revoke_all_sessions: true });
    },
    handleSessionLimitCancel() {
      this.sessionsLimitReached = false;
      this.limitedSessions = [];
      this.credentials.password = '';
    },
    loginWithGoogle() {
      const baseUrl = 'https://accounts.google.com/o/oauth2/auth';
      const clientId = window.osdeskConfig.googleOAuthClientId;
      const redirectUri = window.osdeskConfig.googleOAuthCallbackUrl;
      const responseType = 'code';
      const scope = 'email profile';

      const queryString = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: responseType,
        scope: scope,
      }).toString();

      window.location.href = `${baseUrl}?${queryString}`;
    },
    goToSsoLogin() {
      this.$router.push({ name: 'sso_login' });
    },
  },
};
</script>

<template>
  <div
    class="scroll-smooth h-screen w-full flex flex-col md:flex-row bg-surface overflow-hidden overflow-x-hidden"
  >
    <!-- Auth Shell suppressed per instructions for Transactional Login page -->

    <!-- Left Side: Visual / Testimonial -->
    <section
      class="hidden md:flex md:w-1/2 lg:w-3/5 bg-deep-navy relative items-center justify-center overflow-hidden h-full"
    >
      <div class="relative z-10 px-margin-desktop max-w-2xl">
        <div class="mb-stack-xl">
          <span
            class="font-headline-md text-headline-md font-bold text-surface-bright tracking-tight"
            >OS DESK</span>
        </div>
        <div class="glass-panel p-stack-lg rounded-xl mb-stack-lg">
          <div
            class="flex items-center gap-stack-sm mb-stack-md text-secondary-fixed"
          >
            <span
              class="material-symbols-outlined"
              style="font-variation-settings: 'FILL' 1"
              >stars</span>
            <span class="font-label-md text-label-md uppercase tracking-widest">Enterprise Intelligence</span>
          </div>
          <h2
            class="font-headline-lg text-headline-lg text-white mb-stack-md leading-tight"
          >
            Transforming high-stakes support with autonomous precision.
          </h2>
          <p class="font-body-lg text-body-lg text-surface-variant opacity-90">
            The "Intelligent Architect" for your modern helpdesk. Secure, fluid,
            and always one step ahead of the next ticket.
          </p>
        </div>
        <div class="flex items-center gap-stack-md">
          <div class="flex -space-x-4">
            <img
              class="w-12 h-12 rounded-full border-2 border-deep-navy object-cover"
              data-alt="A professional headshot of a female executive in a modern office, soft high-key lighting, corporate modernism style with deep navy and light blue tones."
              src="/brand-assets/portfolio/stitch_76ce1c51f15f.jpg"
            />
            <img
              class="w-12 h-12 rounded-full border-2 border-deep-navy object-cover"
              data-alt="A professional headshot of a male IT director in a sleek tech environment, shallow depth of field, cool blue color grading, minimalist aesthetic."
              src="/brand-assets/portfolio/stitch_db4a1c76a4c8.jpg"
            />
            <img
              class="w-12 h-12 rounded-full border-2 border-deep-navy object-cover"
              data-alt="A professional headshot of a diverse support lead, bright clean lighting, energetic corporate look with accents of emerald green."
              src="/brand-assets/portfolio/stitch_53f2ae24b7a1.jpg"
            />
          </div>
          <p class="font-caption text-caption text-surface-variant">
            Trusted by 500+ Fortune 1000 enterprises globally.
          </p>
        </div>
      </div>
      <!-- Absolute bottom decoration -->
      <div
        class="absolute bottom-12 left-12 flex gap-4 text-surface-variant/30 font-display-xl select-none pointer-events-none"
      >
        <span>01</span>
        <span>02</span>
        <span>03</span>
      </div>
    </section>
    <!-- Right Side: Login Form -->
    <section
      class="w-full md:w-1/2 lg:w-2/5 flex flex-col items-center bg-surface relative h-full overflow-y-auto overflow-x-hidden p-6 md:p-12"
    >
      <div class="w-full max-w-md my-auto py-8">
        <div class="mb-stack-md text-left">
          <router-link
            to="/"
            class="inline-flex items-center gap-2 text-caption text-on-surface-variant hover:text-primary transition-colors font-medium"
          >
            <span class="material-symbols-outlined text-[16px]">arrow_back</span>
            Back to Home
          </router-link>
        </div>
        <div class="mb-stack-xl text-center md:text-left">
          <div class="md:hidden mb-stack-md">
            <span
              class="font-headline-md text-headline-md font-bold text-deep-navy"
              >OS DESK</span>
          </div>
          <h1
            class="font-headline-lg text-headline-lg text-on-surface mb-stack-xs"
          >
            Welcome back
          </h1>
          <p class="font-body-md text-body-md text-on-surface-variant">
            Access your intelligent AI workspace.
          </p>
        </div>
        <!-- Social/SSO Buttons -->
        <div v-if="showGoogleOAuth || showSamlLogin" class="flex flex-col gap-stack-md mb-stack-lg">
          <button
            v-if="showGoogleOAuth"
            @click="loginWithGoogle"
            class="w-full flex items-center justify-center gap-stack-md py-3 px-4 border border-outline-variant rounded-lg font-label-md text-label-md text-on-surface hover:bg-surface-container transition-all active:scale-[0.98]"
          >
            <svg class="w-5 h-5" viewbox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Sign in with Google
          </button>
          <button
            v-if="showSamlLogin"
            @click="goToSsoLogin"
            class="w-full flex items-center justify-center gap-stack-md py-3 px-4 border border-outline-variant rounded-lg font-label-md text-label-md text-on-surface hover:bg-surface-container transition-all active:scale-[0.98]"
          >
            <span class="material-symbols-outlined text-on-surface-variant">vpn_key</span>
            Sign in with SSO
          </button>
        </div>
        <div v-if="showGoogleOAuth || showSamlLogin" class="relative flex items-center mb-stack-lg">
          <div class="flex-grow border-t border-outline-variant" />
          <span class="flex-shrink mx-4 font-caption text-caption text-outline">or use email</span>
          <div class="flex-grow border-t border-outline-variant" />
        </div>
        <!-- Login Form -->
        <form class="space-y-stack-md" @submit.prevent="submitFormLogin">
          <div class="space-y-stack-xs">
            <label
              class="font-label-md text-label-md text-on-surface"
              for="email"
              >Email Address</label>
            <input
              id="email"
              v-model="credentials.email"
              class="w-full px-4 py-3 bg-white border border-outline-variant rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none transition-all placeholder:text-outline-variant"
              name="email"
              placeholder="name@company.com"
              required=""
              type="email"
            />
          </div>
          <div class="space-y-stack-xs">
            <label
              class="font-label-md text-label-md text-on-surface"
              for="password"
              >Password</label>
            <div class="relative group">
              <input
                id="password"
                v-model="credentials.password"
                class="w-full px-4 py-3 bg-white border border-outline-variant rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none transition-all placeholder:text-outline-variant"
                name="password"
                placeholder="••••••••"
                required=""
                :type="isPasswordVisible ? 'text' : 'password'"
              />
              <button
                @click="isPasswordVisible = !isPasswordVisible"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-primary transition-colors"
                type="button"
              >
                <span class="material-symbols-outlined text-body-md">{{ isPasswordVisible ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </div>
          <div class="flex items-center justify-between py-2">
            <label class="flex items-center gap-2 cursor-pointer select-none">
              <input
                class="w-4 h-4 rounded border-outline-variant text-secondary focus:ring-secondary"
                type="checkbox"
              />
              <span class="font-caption text-caption text-on-surface-variant">Remember me</span>
            </label>
            <router-link
              class="font-caption text-caption text-primary hover:text-secondary font-semibold transition-colors"
              :to="{ name: 'auth_reset_password' }"
            >
              Forgot password?
            </router-link>
          </div>
          <button
            class="w-full bg-deep-navy text-white py-4 rounded-lg font-label-md text-label-md hover:bg-primary transition-all shadow-lg shadow-primary-container/10 active:scale-[0.99] flex items-center justify-center gap-2"
            type="submit"
          >
            Sign In
            <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
          </button>
        </form>
        <div class="mt-stack-xl text-center">
          <p class="font-body-md text-body-md text-on-surface-variant">
            Don't have an account?
            <router-link
              class="text-secondary font-semibold hover:underline"
              to="/app/auth/signup"
            >
              Request access
            </router-link>
          </p>
        </div>
        <!-- Footer Links -->
        <div
          class="mt-stack-xl pt-stack-lg border-t border-outline-variant flex flex-wrap justify-center gap-x-gutter gap-y-stack-xs"
        >
          <router-link
            class="font-caption text-caption text-outline hover:text-on-surface transition-colors"
            to="/privacy"
          >
            Privacy Policy
          </router-link>
          <router-link
            class="font-caption text-caption text-outline hover:text-on-surface transition-colors"
            to="/terms"
          >
            Terms of Service
          </router-link>
          <router-link
            class="font-caption text-caption text-outline hover:text-on-surface transition-colors"
            to="/security"
          >
            Security
          </router-link>
        </div>
      </div>
      <!-- Subtle AI Glow background effect -->
      <div
        class="absolute -z-10 top-1/4 -right-24 w-96 h-96 bg-ai-glow/50 blur-[120px] rounded-full"
      />
    </section>
  </div>
</template>
